import { LoadingState } from '../../components/feedback/LoadingState'
import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { pageTheme,titleTheme,cardTheme } from '../../components/consoleTheme'
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { HeadphonesIcon, Plus, ArrowLeft, Send, MessageCircle, AlertCircle, Check, X } from 'lucide-react';

function getToken() {
  return sessionStorage.getItem('token') || localStorage.getItem('token');
}

async function authFetch(url, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, { ...opts, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

const CATEGORY_LABEL = {
  general: '일반', billing: '결제', technical: '기술', account: '계정', other: '기타'
};
const PRIORITY_LABEL = {
  low: '낮음', normal: '보통', high: '높음', urgent: '긴급'
};
const STATUS_LABEL = {
  open: '열림', pending: '답변완료', resolved: '해결됨', closed: '종료'
};

export default function SupportPage() {
  const [view, setView] = useState('list');     // list | new | detail
  const [activeId, setActiveId] = useState(null);

  return (
    <Wrap>
      <PageTitle><HeadphonesIcon size={22} /> 고객지원</PageTitle>
      {view === 'list' && <TicketList onOpenNew={() => setView('new')} onOpen={(id) => { setActiveId(id); setView('detail'); }} />}
      {view === 'new' && <NewTicket onBack={() => setView('list')} onCreated={(id) => { setActiveId(id); setView('detail'); }} />}
      {view === 'detail' && <TicketDetail id={activeId} onBack={() => setView('list')} />}
    </Wrap>
  );
}

// ============== 목록 ==============
function TicketList({ onOpenNew, onOpen }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await authFetch('/api/support/tickets');
      setTickets(data.tickets || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <Toolbar>
        <Sub>나의 문의 내역</Sub>
        <PrimaryBtn onClick={onOpenNew}><Plus size={16} /> 새 문의 작성</PrimaryBtn>
      </Toolbar>
      {err && <Alert kind="error"><AlertCircle size={14} /> {err}</Alert>}
      {loading ? (
        <LoadingState label="불러오는 중…" />
      ) : tickets.length === 0 ? (
        <Empty>
          <MessageCircle size={36} />
          <div>아직 등록한 문의가 없습니다.</div>
          <small>위 "새 문의 작성" 버튼으로 첫 문의를 남겨보세요.</small>
        </Empty>
      ) : (
        <List>
          {tickets.map(t => (
            <ListItem key={t.ticket_id} onClick={() => onOpen(t.ticket_id)}>
              <ListMain>
                <ListSubject>
                  <StatusDot status={t.status} />
                  {t.subject}
                  {Number(t.message_count) > 1 && <MsgCount>{t.message_count}</MsgCount>}
                </ListSubject>
                <ListMeta>
                  <Tag>{CATEGORY_LABEL[t.category] || t.category}</Tag>
                  <Tag priority={t.priority}>{PRIORITY_LABEL[t.priority] || t.priority}</Tag>
                  <StatusBadge status={t.status}>{STATUS_LABEL[t.status] || t.status}</StatusBadge>
                  <DateText>{new Date(t.updated_at).toLocaleString('ko-KR')}</DateText>
                </ListMeta>
              </ListMain>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );
}

// ============== 신규 작성 ==============
function NewTicket({ onBack, onCreated }) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!subject.trim()) return setErr('제목을 입력해 주세요.');
    if (!body.trim()) return setErr('문의 내용을 입력해 주세요.');
    setSaving(true);
    try {
      const data = await authFetch('/api/support/tickets', {
        method: 'POST',
        body: JSON.stringify({ subject, category, priority, body })
      });
      onCreated(data.ticket_id);
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  return (
    <>
      <Toolbar>
        <BackBtn onClick={onBack}><ArrowLeft size={16} /> 목록</BackBtn>
        <Sub>새 문의 작성</Sub>
        <span />
      </Toolbar>
      <Card>
        <Field><Label>제목</Label>
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="간단한 제목 (예: 결제 영수증 발급 문의)" />
        </Field>
        <Row>
          <Field>
            <Label>카테고리</Label>
            <Select value={category} onChange={e => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </Field>
          <Field>
            <Label>우선순위</Label>
            <Select value={priority} onChange={e => setPriority(e.target.value)}>
              {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </Field>
        </Row>
        <Field><Label>내용</Label>
          <Textarea rows={8} value={body} onChange={e => setBody(e.target.value)} placeholder="문의 내용을 자세히 작성해 주세요." />
        </Field>
        {err && <Alert kind="error"><AlertCircle size={14} /> {err}</Alert>}
        <CardFooter>
          <SecondaryBtn onClick={onBack} disabled={saving}>취소</SecondaryBtn>
          <PrimaryBtn onClick={submit} disabled={saving}>
            {saving ? '등록 중...' : '문의 등록'}
          </PrimaryBtn>
        </CardFooter>
      </Card>
    </>
  );
}

// ============== 상세 (대화) ==============
function TicketDetail({ id, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await authFetch(`/api/support/tickets/${id}`);
      setTicket(data.ticket);
      setMessages(data.messages || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await authFetch(`/api/support/tickets/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body: reply })
      });
      setReply('');
      await load();
    } catch (e) {
      setErr(e.message);
    }
    setSending(false);
  };

  const closeTicket = async () => {
    if (!window.confirm('이 문의를 종료하시겠습니까? 더 이상 답변할 수 없습니다.')) return;
    try {
      await authFetch(`/api/support/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'closed' })
      });
      await load();
    } catch (e) {
      setErr(e.message);
    }
  };

  if (loading) return <LoadingState label="불러오는 중…" />;
  if (!ticket) return <Empty>티켓을 찾을 수 없습니다.</Empty>;

  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <>
      <Toolbar>
        <BackBtn onClick={onBack}><ArrowLeft size={16} /> 목록</BackBtn>
        <Sub>#{ticket.ticket_id}</Sub>
        <span />
      </Toolbar>
      <DetailHeader>
        <DetailSubject>{ticket.subject}</DetailSubject>
        <DetailMeta>
          <Tag>{CATEGORY_LABEL[ticket.category]}</Tag>
          <Tag priority={ticket.priority}>{PRIORITY_LABEL[ticket.priority]}</Tag>
          <StatusBadge status={ticket.status}>{STATUS_LABEL[ticket.status]}</StatusBadge>
          <DateText>{new Date(ticket.created_at).toLocaleString('ko-KR')}</DateText>
          {!isClosed && (
            <CloseBtn onClick={closeTicket} title="이 문의를 종료합니다">
              <X size={12} /> 종료
            </CloseBtn>
          )}
        </DetailMeta>
      </DetailHeader>

      <Chat>
        {messages.map(m => (
          <Bubble key={m.message_id} role={m.sender_role}>
            <BubbleHeader>
              <strong>{m.sender_name || (m.sender_role === 'admin' ? '관리자' : '사용자')}</strong>
              <small>{new Date(m.created_at).toLocaleString('ko-KR')}</small>
            </BubbleHeader>
            <BubbleBody>{m.body}</BubbleBody>
          </Bubble>
        ))}
      </Chat>

      {err && <Alert kind="error"><AlertCircle size={14} /> {err}</Alert>}

      {!isClosed ? (
        <ReplyBox>
          <Textarea
            rows={3}
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="답변을 입력하세요. (Ctrl+Enter 로 전송)"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendReply();
            }}
          />
          <PrimaryBtn onClick={sendReply} disabled={sending || !reply.trim()}>
            <Send size={14} /> {sending ? '전송 중...' : '전송'}
          </PrimaryBtn>
        </ReplyBox>
      ) : (
        <ClosedNote>
          <Check size={14} /> 이 문의는 {STATUS_LABEL[ticket.status]} 처리되어 더 이상 답변할 수 없습니다.
        </ClosedNote>
      )}
    </>
  );
}

// ============== Styled ==============
const Wrap = styled.div`
 ${pageTheme}

  padding: 28px 36px 60px;
  max-width: 980px;
  margin: 0 auto;
  text-align: left;
  @media(max-width:768px){padding:1rem 1rem 2rem;}
`;
const PageTitle = styled.h1`
 ${titleTheme}
 &&{margin-bottom:28px;}

  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  @media(max-width:768px){flex-wrap:wrap;gap:.75rem;}
`;
const Sub = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;
const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const SecondaryBtn = styled.button`
  padding: 8px 16px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;
const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 60px 20px;
  color: #9ca3af;
  text-align: center;
  small { color: #9ca3af; }
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ListItem = styled.div`
  display: flex;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 18px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #93c5fd;
    box-shadow: 0 2px 8px rgba(59,130,246,0.08);
  }
`;
const ListMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const ListSubject = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const MsgCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  padding: 0 6px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;
const ListMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status }) =>
    status === 'open' ? '#10b981' :
    status === 'pending' ? 'var(--accent)' :
    status === 'resolved' ? '#a3a3a3' :
    status === 'closed' ? '#9ca3af' : '#d1d5db'};
`;
const Tag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${({ priority }) =>
    priority === 'urgent' ? '#fee2e2' :
    priority === 'high' ? '#fef3c7' :
    priority === 'low' ? '#f3f4f6' : '#f3f4f6'};
  color: ${({ priority }) =>
    priority === 'urgent' ? '#991b1b' :
    priority === 'high' ? '#92400e' :
    priority === 'low' ? '#6b7280' : '#4b5563'};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;
const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'open' ? '#d1fae5' :
    status === 'pending' ? '#dbeafe' :
    status === 'resolved' ? '#f3f4f6' :
    status === 'closed' ? '#f3f4f6' : '#f3f4f6'};
  color: ${({ status }) =>
    status === 'open' ? '#065f46' :
    status === 'pending' ? '#1e40af' :
    status === 'resolved' ? '#6b7280' :
    status === 'closed' ? '#6b7280' : '#6b7280'};
`;
const DateText = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-left: auto;
`;
const Card = styled.div`
 ${cardTheme}

  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;
const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
`;
const Input = styled.input`
  padding: 9px 11px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
`;
const Select = styled.select`
  padding: 9px 11px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  outline: none;
`;
const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: vertical;
  &:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
`;
const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;
const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${({ kind }) => kind === 'error' ? '#fee2e2' : '#dbeafe'};
  color: ${({ kind }) => kind === 'error' ? '#991b1b' : '#1e40af'};
  border: 1px solid ${({ kind }) => kind === 'error' ? '#fca5a5' : '#93c5fd'};
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 12px;
`;
const DetailHeader = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px 10px 0 0;
  padding: 18px 22px;
  border-bottom: none;
`;
const DetailSubject = styled.h2`
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
`;
const DetailMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: white;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  margin-left: auto;
  &:hover { background: #fee2e2; }
`;
const Chat = styled.div`
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-top: 1px solid #f0f0f0;
  border-radius: 0 0 10px 10px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 220px;
  max-height: 60vh;
  overflow-y: auto;
`;
const Bubble = styled.div`
  align-self: ${({ role }) => role === 'admin' ? 'flex-start' : 'flex-end'};
  max-width: 75%;
  background: ${({ role }) => role === 'admin' ? '#eff6ff' : 'white'};
  border: 1px solid ${({ role }) => role === 'admin' ? '#bfdbfe' : '#e5e7eb'};
  border-radius: 10px;
  padding: 10px 14px;
`;
const BubbleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  strong { font-size: 12px; color: #374151; }
  small { font-size: 11px; color: #9ca3af; }
`;
const BubbleBody = styled.div`
  font-size: 13px;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
`;
const ReplyBox = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  align-items: flex-end;
  textarea { flex: 1; }
`;
const ClosedNote = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  font-size: 13px;
  margin-top: 16px;
  justify-content: center;
`;
